import { AlertCircle, Download, FileCheck2, FileUp, LoaderCircle, Trash2 } from "lucide-react";
import { type ChangeEvent, useId, useRef, useState } from "react";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ApiError } from "@/lib/api";
import { DOCUMENTO_ACREDITATIVO_ACCEPT, type DocumentoAcreditativo } from "@/lib/obras";
import { MENSAJE_REQUIERE_CONEXION } from "@/lib/offline-policy";
import { useOnlineStatus } from "@/lib/online-status";

/** formatearTamano pinta un tamaño en bytes con la unidad legible (B, KB o MB). */
export function formatearTamano(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const kb = bytes / 1024;
  if (kb < 1024) {
    return `${kb.toLocaleString("es-ES", { maximumFractionDigits: 1 })} KB`;
  }
  return `${(kb / 1024).toLocaleString("es-ES", { maximumFractionDigits: 1 })} MB`;
}

/** formatearDia pinta la fecha de subida como DD/MM/AAAA. */
function formatearDia(iso: string): string {
  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) {
    return "—";
  }
  return fecha.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
}

// mensajeDeError traduce el fallo de una acción a lo que ve el usuario. Los errores tipados del
// servidor (TIPO_INVALIDO, ARCHIVO_DEMASIADO_GRANDE, …) traen su propio mensaje.
function mensajeDeError(err: unknown, porDefecto: string): string {
  if (err instanceof ApiError && err.message) {
    return err.message;
  }
  return porDefecto;
}

/**
 * DocumentoAcreditativoPanel pinta y gestiona la documentación acreditativa de UNA persona
 * (#435, R13): su estado (sin documento, o nombre, tamaño y «subido el … por …») y las acciones
 * subir, sustituir, descargar y borrar —este último con confirmación—.
 *
 * Es PRESENTACIONAL respecto al destino: recibe las acciones ya ligadas a su ruta, porque lo
 * comparten la ficha del usuario (`/usuarios`, ADMIN) y la configuración de la obra (ruta del
 * miembro, ADMIN o ADMIN_OBRA). Vive en `components/ui`, como ImportButton o ExportButton,
 * porque la regla del repo prohíbe que una feature importe de otra.
 *
 * Todas las acciones van al SERVIDOR y no se encolan: sin red confirmada se deshabilitan con
 * «Requiere conexión» (mismo criterio que la gestión de usuarios, prohibida offline por
 * `offline-policy.ts`). El gate real es el backend.
 */
export function DocumentoAcreditativoPanel({
  documento,
  onSubir,
  onDescargar,
  onBorrar,
}: {
  documento: DocumentoAcreditativo | null | undefined;
  onSubir: (archivo: File) => Promise<unknown>;
  onDescargar: () => Promise<unknown>;
  onBorrar: () => Promise<unknown>;
}) {
  const online = useOnlineStatus();
  const idArchivo = useId();
  const archivoRef = useRef<HTMLInputElement>(null);
  const [enCurso, setEnCurso] = useState<"subir" | "descargar" | "borrar" | null>(null);
  const [confirmarBorrado, setConfirmarBorrado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deshabilitado = !online || enCurso !== null;
  const titulo = online ? undefined : MENSAJE_REQUIERE_CONEXION;

  async function ejecutar(
    accion: "subir" | "descargar" | "borrar",
    fn: () => Promise<unknown>,
    porDefecto: string,
  ) {
    setError(null);
    setEnCurso(accion);
    try {
      await fn();
      if (accion === "borrar") {
        setConfirmarBorrado(false);
      }
    } catch (err) {
      setError(mensajeDeError(err, porDefecto));
    } finally {
      setEnCurso(null);
    }
  }

  function onArchivoElegido(e: ChangeEvent<HTMLInputElement>) {
    const archivo = e.target.files?.[0];
    // Se vacía el input para que volver a elegir el MISMO archivo (tras un error) dispare otra
    // vez el cambio.
    e.target.value = "";
    if (!archivo) {
      return;
    }
    void ejecutar("subir", () => onSubir(archivo), "No se pudo subir el documento.");
  }

  const elegirArchivo = () => archivoRef.current?.click();

  return (
    <div className="flex flex-col gap-3">
      <input
        ref={archivoRef}
        id={idArchivo}
        type="file"
        accept={DOCUMENTO_ACREDITATIVO_ACCEPT}
        aria-label="Archivo de la documentación acreditativa"
        className="sr-only"
        tabIndex={-1}
        disabled={deshabilitado}
        onChange={onArchivoElegido}
      />

      {documento ? (
        <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3">
          <div className="flex items-start gap-2">
            <FileCheck2 className="mt-0.5 h-5 w-5 shrink-0 text-green-700" aria-hidden="true" />
            <div className="flex min-w-0 flex-col">
              <span className="break-all font-medium text-slate-900">{documento.nombre}</span>
              <span className="text-slate-500 text-sm">
                {formatearTamano(documento.tamano)} · subido el {formatearDia(documento.subido_en)}{" "}
                por {documento.subido_por_nombre || "—"}
              </span>
            </div>
          </div>

          {confirmarBorrado ? (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
              <span className="text-red-800 text-sm">
                ¿Seguro que quieres borrar la documentación acreditativa? Si su rol la exige, no
                podrá operar LOTOs hasta que se suba otra.
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmarBorrado(false)}
                  disabled={enCurso !== null}
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  title={titulo}
                  disabled={deshabilitado}
                  onClick={() =>
                    void ejecutar("borrar", onBorrar, "No se pudo borrar el documento.")
                  }
                >
                  {enCurso === "borrar" ? (
                    <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  )}
                  Sí, borrar
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                title={titulo}
                disabled={deshabilitado}
                onClick={() =>
                  void ejecutar("descargar", onDescargar, "No se pudo descargar el documento.")
                }
              >
                {enCurso === "descargar" ? (
                  <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Download className="h-4 w-4" aria-hidden="true" />
                )}
                Descargar
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                title={titulo}
                disabled={deshabilitado}
                onClick={elegirArchivo}
              >
                {enCurso === "subir" ? (
                  <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <FileUp className="h-4 w-4" aria-hidden="true" />
                )}
                Sustituir
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                title={titulo}
                disabled={deshabilitado}
                onClick={() => setConfirmarBorrado(true)}
              >
                <Trash2 className="h-4 w-4 text-red-600" aria-hidden="true" />
                Borrar
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 border-dashed bg-slate-50 p-3">
          <span className="text-slate-600">Sin documentación acreditativa</span>
          <Button
            type="button"
            size="sm"
            title={titulo}
            disabled={deshabilitado}
            onClick={elegirArchivo}
          >
            {enCurso === "subir" ? (
              <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <FileUp className="h-4 w-4" aria-hidden="true" />
            )}
            Subir documento
          </Button>
        </div>
      )}

      <p className="text-slate-500 text-sm">PDF, JPEG o PNG, hasta 8 MiB.</p>

      {error ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </Alert>
      ) : null}
    </div>
  );
}
